import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Users, Repeat, TrendingUp, MapPin, Heart, Sparkles } from "lucide-react";
import { formatDate } from "@/lib/format";

export default function VenueAudience() {
    const { venue } = useOutletContext();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(true);
        endpoints.venueAudience(venue.id).then(setData).finally(() => setLoading(false));
    }, [venue.id]);

    if (loading || !data) return <div className="p-8 font-mono text-xs text-[#666]" data-testid="venue-audience-loading">Loading…</div>;
    const s = data.summary;
    const f = data.followers || {};

    return (
        <div className="pm-fade" data-testid="venue-audience-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Audience</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Venue insights</h1>
            </header>

            <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                <Kpi icon={Users} label="Unique attendees" value={s.unique_attendees} testId="va-unique" />
                <Kpi icon={Repeat} label="Returning" value={s.returning_attendees} testId="va-returning" />
                <Kpi icon={TrendingUp} label="Reg → attend" value={`${s.registration_to_attendance_rate}%`} testId="va-conversion" />
                <Kpi icon={MapPin} label="Completed events" value={s.total_completed_events} testId="va-completed" />
            </section>

            {/* Followers analytics section */}
            <section className="border-b border-[#2a2a2a]" data-testid="va-followers-section">
                <div className="px-4 md:px-8 py-4 flex items-baseline justify-between">
                    <div>
                        <div className="text-[10px] font-mono uppercase text-[#666]">Followers</div>
                        <h2 className="font-display font-black uppercase text-2xl">Venue followers</h2>
                    </div>
                    <div className="text-[10px] font-mono uppercase text-[#666] max-w-xs text-right">Event-window growth is time-window correlation, not causal impact.</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#2a2a2a] border-t border-[#2a2a2a]">
                    <Kpi icon={Heart} label="Followers" value={f.current_followers || 0} testId="va-followers-total" />
                    <Kpi icon={TrendingUp} label="New · 7 days" value={f.new_last_7_days || 0} testId="va-followers-7" />
                    <Kpi icon={TrendingUp} label="New · 30 days" value={f.new_last_30_days || 0} testId="va-followers-30" />
                    <Kpi icon={Repeat} label="Returning venue attendees" value={f.returning_venue_attendees || 0} testId="va-returning-venue-attendees" />
                    <Kpi icon={Sparkles} label="Followers who attended" value={f.followers_who_attended || 0} testId="va-followers-who-attended" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#2a2a2a] border-t border-[#2a2a2a]">
                    <div className="bg-[#0a0a0a] p-6" data-testid="va-follower-top-events">
                        <div className="text-[10px] font-mono uppercase text-[#666]">Growth</div>
                        <h3 className="mt-1 font-display font-black uppercase text-xl md:text-2xl mb-3">Events driving follower growth</h3>
                        {(f.top_events_by_growth || []).length === 0 ? (
                            <div className="text-sm text-[#a3a3a3]">No completed events yet.</div>
                        ) : (f.top_events_by_growth || []).map((e) => (
                            <div key={e.event_id} className="border-t border-[#2a2a2a] py-2 flex justify-between items-center" data-testid={`va-follower-event-${e.event_id}`}>
                                <div className="min-w-0">
                                    <div className="text-sm font-display font-bold uppercase truncate">{e.event_name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666]">{formatDate(e.event_date)}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display font-black text-xl text-[#ff4400]">+{e.window_total}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666]">follows in window · {e.direct_event_follows} direct</div>
                                </div>
                            </div>
                        ))}
                        <div className="mt-4 text-[10px] font-mono uppercase text-[#666]">Window: 7 days before → 7 days after event date (UTC).</div>
                    </div>
                    <div className="bg-[#0a0a0a] p-6" data-testid="va-follower-geo">
                        <div className="text-[10px] font-mono uppercase text-[#666]">Where followers are from</div>
                        <h3 className="mt-1 font-display font-black uppercase text-xl md:text-2xl mb-3">Follower cities</h3>
                        {(f.by_city || []).length === 0 ? <div className="text-sm text-[#a3a3a3]">No location data yet.</div>
                        : (f.by_city || []).map((c) => (
                            <div key={c.city} className="flex justify-between items-center py-1 text-xs font-mono uppercase" data-testid={`va-follower-city-${c.city}`}>
                                <span>{c.city}</span><span className="text-[#a3a3a3]">{c.count}</span>
                            </div>
                        ))}
                        <h3 className="mt-4 font-display font-black uppercase text-xl md:text-2xl mb-3">Follower countries</h3>
                        {(f.by_country || []).map((c) => (
                            <div key={c.country} className="flex justify-between items-center py-1 text-xs font-mono uppercase" data-testid={`va-follower-country-${c.country}`}>
                                <span>{c.country}</span><span className="text-[#a3a3a3]">{c.count}</span>
                            </div>
                        ))}
                    </div>
                </div>
                {(f.recent_follows || []).length > 0 && (
                    <div className="bg-[#0a0a0a] p-6 border-t border-[#2a2a2a]" data-testid="va-recent-follows">
                        <div className="text-[10px] font-mono uppercase text-[#666]">Recent</div>
                        <h3 className="mt-1 font-display font-black uppercase text-xl md:text-2xl mb-3">Latest follows</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {f.recent_follows.map((r) => (
                                <div key={r.id} className="border border-[#2a2a2a] p-3 text-xs" data-testid={`va-recent-follow-${r.id}`}>
                                    <div className="font-display font-bold uppercase truncate">{r.fan_name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">
                                        {r.fan_city ? `${r.fan_city}, ${r.fan_country || ""}` : "—"} · via {r.source_label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#2a2a2a]">
                <div className="bg-[#0a0a0a] p-6" data-testid="va-best-events">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Highlights</div>
                    <h2 className="mt-1 font-display font-black uppercase text-xl md:text-2xl mb-3">Best-performing events</h2>
                    {data.best_events.length === 0 ? <div className="text-sm text-[#a3a3a3]">No completed events yet.</div>
                    : data.best_events.map((e) => (
                        <div key={e.id} className="border-t border-[#2a2a2a] py-2 flex justify-between items-center" data-testid={`va-best-${e.id}`}>
                            <div className="min-w-0">
                                <div className="text-sm font-display font-bold uppercase truncate">{e.name}</div>
                                <div className="text-[10px] font-mono uppercase text-[#666]">{formatDate(e.date)}</div>
                            </div>
                            <div className="text-right">
                                <div className="font-display font-black text-xl text-[#ff4400]">{e.attendees}</div>
                                <div className="text-[10px] font-mono uppercase text-[#666]">/{e.registrations}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="bg-[#0a0a0a] p-6" data-testid="va-geo">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Where they come from</div>
                    <h2 className="mt-1 font-display font-black uppercase text-xl md:text-2xl mb-3">Cities</h2>
                    {data.by_city.length === 0 ? <div className="text-sm text-[#a3a3a3]">No location data yet.</div>
                    : data.by_city.map((c) => (
                        <div key={c.city} className="flex justify-between items-center py-1 text-xs font-mono uppercase" data-testid={`va-city-${c.city}`}>
                            <span>{c.city}</span><span className="text-[#a3a3a3]">{c.count}</span>
                        </div>
                    ))}
                    <h2 className="mt-4 font-display font-black uppercase text-xl md:text-2xl mb-3">Countries</h2>
                    {data.by_country.map((c) => (
                        <div key={c.country} className="flex justify-between items-center py-1 text-xs font-mono uppercase" data-testid={`va-country-${c.country}`}>
                            <span>{c.country}</span><span className="text-[#a3a3a3]">{c.count}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Kpi({ icon: Icon, label, value, testId }) {
    return (
        <div className="bg-[#0a0a0a] p-5" data-testid={testId}>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#666]"><Icon className="w-3 h-3" /> {label}</div>
            <div className="mt-2 font-display font-black uppercase tracking-tighter text-2xl md:text-3xl">{value}</div>
        </div>
    );
}
